import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import type { Database, Json } from '../generated';

// =========================================================================
// Carrier account shape exposed to the operator app.
//
// SECURITY: this type is intentionally a SUBSET of the underlying
// shipping.carrier_accounts row. The generated `Database` type still
// references columns the authenticated role cannot SELECT
// (`vault_secret_id`) -- we never select or expose those. The only
// echo of the API key surfaced to the client is `secret_last_four`, the
// same mask pattern used for credit cards.
// =========================================================================
export interface CarrierAccount {
  id: string;
  org_id: string;
  store_id: string;
  carrier: Database['shipping']['Enums']['carrier'];
  label: string;
  is_active: boolean;
  secret_last_four: string | null;
  secret_set_at: string | null;
  metadata: Database['shipping']['Tables']['carrier_accounts']['Row']['metadata'];
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

export type ShippingLabel = Database['shipping']['Tables']['labels']['Row'];
export type ShippingLabelInsert = Database['shipping']['Tables']['labels']['Insert'];
export type Carrier = Database['shipping']['Enums']['carrier'];
export type LabelStatus = Database['shipping']['Enums']['label_status'];

/**
 * One row of the platform `shipping.carriers` catalog. The list is
 * small and reads through `listShippingCarriers` use a defensive
 * `.limit()` per the catalog-table exception in
 * `ai-rules/pagination-required.md`.
 */
export interface ShippingCarrierRow {
  code: string;
  display_name: string;
  /**
   * Optional tracking URL template with `{tracking_number}` as the
   * substitution token. UI consumers should call
   * `formatTrackingUrl(template, trackingNumber)` rather than doing
   * the substitution inline.
   */
  tracking_url_template: string | null;
  is_active: boolean;
  sort_order: number;
}

/**
 * Returns the active carriers (UPS / USPS / FedEx / DHL / ..., plus the
 * `other` sentinel) ordered by `sort_order, display_name`. Used by:
 *
 * - The vendor "submit tracking" dropdown in `apps/sell`.
 * - The buyer "linkify tracking number" path in `apps/shop` — the
 *   template comes from the carrier row, the substitution happens
 *   client-side via `formatTrackingUrl`.
 *
 * The catalog is platform-scope (no org_id), and RLS allows every
 * authenticated session to read; this function does not require an
 * `ActiveContext`.
 */
export async function listShippingCarriers(): Promise<ShippingCarrierRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('shipping')
    .from('carriers')
    .select('code, display_name, tracking_url_template, is_active, sort_order')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('display_name', { ascending: true })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as ShippingCarrierRow[];
}

/**
 * Substitute `{tracking_number}` in a carrier template into a real
 * URL. Returns null when either input is missing.
 *
 * Intentionally does NOT URL-encode the tracking number — carriers
 * use the bare alphanumeric form in their public tracking URLs (e.g.
 * UPS expects `1Z…` verbatim). If a tracking format ever requires
 * encoding, switch this to `encodeURIComponent` and add a per-carrier
 * override in the template.
 */
export function formatTrackingUrl(
  template: string | null | undefined,
  trackingNumber: string | null | undefined,
): string | null {
  if (!template || !trackingNumber) return null;
  return template.replace('{tracking_number}', trackingNumber.trim());
}

// Explicit column list -- never use `select('*')` here. The
// vault_secret_id column carries column-level REVOKE for authenticated,
// but spelling out the safe set is the documentation that matters.
const CARRIER_ACCOUNT_PUBLIC_COLS =
  'id, org_id, store_id, carrier, label, is_active, secret_last_four, secret_set_at, metadata, archived_at, archived_by, created_at, updated_at';

// ---------- carrier accounts ---------------------------------------------

export async function listCarrierAccounts(
  ctx: ActiveContext,
): Promise<CarrierAccount[]> {
  const { orgId, storeId } = requireContext(ctx);
  const { data, error } = await supabase()
    .schema('shipping')
    .from('carrier_accounts')
    .select(CARRIER_ACCOUNT_PUBLIC_COLS)
    .eq('org_id', orgId)
    .eq('store_id', storeId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as unknown as CarrierAccount[];
}

export interface ConnectCarrierInput {
  carrier: Carrier;
  label: string;
  /**
   * Raw API key as issued by the carrier (e.g. Shippo's
   * `shippo_live_<hex>`). Travels exactly one hop: from this function
   * straight to the SECURITY DEFINER RPC `shipping.rpc_connect_carrier`,
   * which stores it in `vault.secrets` and returns only the last four
   * characters for UI masking. The key is NEVER written to the
   * `carrier_accounts` row plaintext and is NEVER returned on any
   * subsequent fetch.
   */
  apiKey: string;
}

/**
 * Connect a new carrier account. The API key is forwarded once into
 * Supabase Vault via the server-side RPC and immediately discarded by
 * this client; no Supabase Studio / Vault dashboard step is required.
 */
export async function connectCarrierAccount(
  ctx: ActiveContext,
  input: ConnectCarrierInput,
): Promise<CarrierAccount> {
  const { storeId } = requireContext(ctx);
  const apiKey = input.apiKey.trim();
  if (!apiKey) throw new Error('API key required');
  const { data, error } = await supabase()
    .schema('shipping')
    .rpc('rpc_connect_carrier', {
      p_store_id: storeId,
      p_carrier: input.carrier,
      p_label: input.label.trim(),
      p_secret: apiKey,
    });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.id) throw new Error('Carrier connect returned no row');
  // Refetch the full public projection so the cache sees the same shape
  // the list query produces.
  const { data: full, error: fullErr } = await supabase()
    .schema('shipping')
    .from('carrier_accounts')
    .select(CARRIER_ACCOUNT_PUBLIC_COLS)
    .eq('id', row.id)
    .single();
  if (fullErr) throw fullErr;
  return full as unknown as CarrierAccount;
}

export async function rotateCarrierApiKey(
  ctx: ActiveContext,
  input: { id: string; apiKey: string },
): Promise<void> {
  requireContext(ctx);
  const apiKey = input.apiKey.trim();
  if (!apiKey) throw new Error('API key required');
  const { error } = await supabase()
    .schema('shipping')
    .rpc('rpc_rotate_carrier_secret', {
      p_account_id: input.id,
      p_secret: apiKey,
    });
  if (error) throw error;
}

export async function archiveCarrierAccount(
  ctx: ActiveContext,
  id: string,
): Promise<void> {
  requireContext(ctx);
  // Archive + vault delete are atomic inside the RPC; falling back to
  // a direct UPDATE here would leave the vault.secrets row stranded.
  const { error } = await supabase()
    .schema('shipping')
    .rpc('rpc_archive_carrier', { p_account_id: id });
  if (error) throw error;
}

// ---------- store-level shipping defaults --------------------------------

export interface ShipFromAddress {
  name?: string;
  company?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  email?: string;
  phone?: string;
}

export interface DefaultParcel {
  length_in?: number;
  width_in?: number;
  height_in?: number;
  weight_oz?: number;
}

export interface StoreShippingSettings {
  store_id: string;
  org_id: string;
  ship_from: ShipFromAddress;
  default_parcel: DefaultParcel | null;
  created_at: string;
  updated_at: string;
}

/**
 * Returns the store's default ship-from address and parcel. Resolves to
 * null when the operator has not configured shipping defaults yet (RLS
 * returns zero rows for the current store).
 */
export async function getStoreShippingSettings(
  ctx: ActiveContext,
): Promise<StoreShippingSettings | null> {
  const { storeId } = requireContext(ctx);
  const { data, error } = await supabase()
    .schema('shipping')
    .from('store_settings')
    .select('store_id, org_id, ship_from, default_parcel, created_at, updated_at')
    .eq('store_id', storeId)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as StoreShippingSettings | null) ?? null;
}

export async function upsertStoreShippingSettings(
  ctx: ActiveContext,
  input: { shipFrom: ShipFromAddress; defaultParcel?: DefaultParcel | null },
): Promise<StoreShippingSettings> {
  const { storeId } = requireContext(ctx);
  const { data, error } = await supabase()
    .schema('shipping')
    .rpc('rpc_upsert_store_settings', {
      p_store_id: storeId,
      p_ship_from: input.shipFrom as unknown as Json,
      p_default_parcel: (input.defaultParcel ?? null) as unknown as Json | null,
    });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as unknown as StoreShippingSettings;
}

// ---------- labels -------------------------------------------------------

export async function listLabelsForOrder(
  ctx: ActiveContext,
  orderId: string,
): Promise<ShippingLabel[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('shipping')
    .from('labels')
    .select('*')
    .eq('order_id', orderId)
    .order('purchased_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return data ?? [];
}

export async function listLabelsTop(
  ctx: ActiveContext,
  limit = 50,
): Promise<ShippingLabel[]> {
  const { orgId, storeId } = requireContext(ctx);
  const { data, error } = await supabase()
    .schema('shipping')
    .from('labels')
    .select('*')
    .eq('org_id', orgId)
    .eq('store_id', storeId)
    .order('purchased_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function getLabelById(
  ctx: ActiveContext,
  id: string,
): Promise<ShippingLabel | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('shipping')
    .from('labels')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

// ---------- edge function bridge ----------------------------------------

export interface ShippingShipmentAddress {
  name: string;
  company?: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  email?: string;
  phone?: string;
}

export interface ShippingParcel {
  length_in: number;
  width_in: number;
  height_in: number;
  weight_oz: number;
}

export interface ShippingShipment {
  fromAddress: ShippingShipmentAddress;
  toAddress: ShippingShipmentAddress;
  parcel: ShippingParcel;
}

export interface ShippingRate {
  id: string;
  carrier: Carrier;
  service: string;
  amountCents: number;
  currency: string;
  estimatedDays?: number;
}

/**
 * Invokes the `shipping-label` Edge Function via the operator app's
 * authenticated supabase client. The function reads the carrier API key
 * from Supabase Vault and calls the carrier adapter; the key never leaves
 * the server.
 */
async function invokeShippingLabel<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase().functions.invoke('shipping-label', { body });
  if (error) throw error;
  const payload = data as { ok: boolean; error?: string } & T;
  if (!payload.ok) throw new Error(payload.error ?? 'Shipping function failed');
  return payload as T;
}

export async function quoteShipping(
  ctx: ActiveContext,
  input: { carrierAccountId: string; shipment: ShippingShipment },
): Promise<ShippingRate[]> {
  requireContext(ctx);
  const res = await invokeShippingLabel<{ rates: ShippingRate[] }>({
    action: 'quote',
    carrierAccountId: input.carrierAccountId,
    shipment: input.shipment,
  });
  return res.rates;
}

export interface PurchaseShippingLabelResult {
  label: ShippingLabel;
  /** Order totals after the rate was rolled in. Null if rate was zero. */
  orderTotals: {
    order_id: string;
    shipping_cents: number;
    subtotal_cents: number;
    total_cents: number;
  } | null;
  /** Non-fatal warning (e.g. label saved but order totals failed). */
  warning?: string | null;
}

export async function purchaseShippingLabel(
  ctx: ActiveContext,
  input: {
    carrierAccountId: string;
    orderId: string;
    shipment: ShippingShipment;
    rateId: string;
    /** Cents copy of the chosen rate (from the original quote). */
    rateCents: number;
    rateCurrency?: string;
  },
): Promise<PurchaseShippingLabelResult> {
  requireContext(ctx);
  const res = await invokeShippingLabel<{
    label: ShippingLabel;
    orderTotals: PurchaseShippingLabelResult['orderTotals'];
    warning?: string | null;
  }>({
    action: 'purchase',
    carrierAccountId: input.carrierAccountId,
    orderId: input.orderId,
    shipment: input.shipment,
    rateId: input.rateId,
    rateCents: input.rateCents,
    rateCurrency: input.rateCurrency,
  });
  return {
    label: res.label,
    orderTotals: res.orderTotals ?? null,
    warning: res.warning ?? null,
  };
}

export interface RefundShippingLabelResult {
  label: ShippingLabel;
  /** Verbatim carrier status (e.g. Shippo: SUCCESS | QUEUED | PENDING). */
  refundStatus: string;
  /** Order totals after the rate was reversed. Null if refund is still pending. */
  orderTotals: {
    order_id: string;
    shipping_cents: number;
    subtotal_cents: number;
    total_cents: number;
  } | null;
}

/**
 * Asks the carrier to refund a purchased label. Resolves with the new
 * label row (status either `refunded` for synchronous success or
 * `refund_pending` for carriers that hold the request in their own
 * pipeline). Throws if the carrier rejects the refund (409); the
 * thrown error's message is the carrier's verbatim reason.
 */
export async function refundShippingLabel(
  ctx: ActiveContext,
  input: { carrierAccountId: string; labelId: string },
): Promise<RefundShippingLabelResult> {
  requireContext(ctx);
  const res = await invokeShippingLabel<{
    label: ShippingLabel;
    refundStatus: string;
    orderTotals: RefundShippingLabelResult['orderTotals'];
  }>({
    action: 'refund',
    carrierAccountId: input.carrierAccountId,
    labelId: input.labelId,
  });
  return {
    label: res.label,
    refundStatus: res.refundStatus,
    orderTotals: res.orderTotals ?? null,
  };
}

/** @deprecated Use refundShippingLabel. Kept for callers that haven't migrated. */
export async function voidShippingLabel(
  ctx: ActiveContext,
  input: { carrierAccountId: string; labelId: string },
): Promise<void> {
  await refundShippingLabel(ctx, input);
}
